<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useQueryStore } from '@/stores/query'
import { useMainStore } from '@/stores/main'
import { useDataSourcesStore } from '@product/stores/dataSources'
import SidebarExpandable from '@/components/SidebarExpandable.vue'

const developerEmails = ['andreas@webscout.io', 'admin@webscout.dev']

const userStore = useUserStore()
const queryStore = useQueryStore()
const mainStore = useMainStore()
const dataSourcesStore = useDataSourcesStore()

const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})

// Live on/off state for the selector-context dot items
function enrichmentOn(slug) {
  return dataSourcesStore.enrichments.find(e => e.slug === slug)?.status === 'active'
}

// Whether an enrichment exposes a dashboard page (backend Statable). Shortcuts
// that do become clickable links to /selector/integration/<slug>.
function enrichmentHasDashboard(slug) {
  return !!dataSourcesStore.enrichments.find(e => e.slug === slug)?.hasDashboard
}

// Live presence of a source group by slug, for the Flow Sources shortcut items
function capFirst(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function groupBySlug(slug) {
  return dataSourcesStore.sourceGroups.find(g => g.name === slug)
}

// Resolve a Flow Sources shortcut slug to its detail route: a source group
// links to /flow/group/<id>, a standalone source to /flow/source/<id>.
function sourceRouteForSlug(slug) {
  const group = dataSourcesStore.sourceGroups.find(g => g.name === slug)
  if (group) return { name: 'FlowGroup', params: { id: group.id } }
  const source = dataSourcesStore.standaloneSources.find(s => s.name === slug)
  if (source) return { name: 'FlowSource', params: { id: source.id } }
  return null
}

// Admin tools — popped out from the admin button (admins only)
const adminTools = [
  { icon: ['fas', 'lightbulb'], label: 'guides', route: '/guides' },
  { icon: ['fas', 'address-card'], label: 'providers', route: '/admin/providers' },
  { icon: ['fas', 'users'], label: 'accounts', route: '/admin/accounts' },
  { icon: ['fas', 'building'], label: 'organizations', route: '/admin/organizations' },
  { icon: ['fas', 'bullhorn'], label: 'notifications', route: '/admin/notifications' },
  { icon: ['fas', 'newspaper'], label: 'news', route: '/admin/news' }
]

// Full nav definition, rendered as one loop of sections.
// Item shape: { label, icon, route?, children?, action?, dot?, badge?,
//               shortcut?, guide?, visible? }
const sections = computed(() => {
  const secs = [
    {
      label: 'Platform',
      items: [
        { label: 'Home', icon: ['fas', 'house'], route: '/home' },
        { label: 'Selector Search', icon: ['fas', 'magnifying-glass'], route: '/search/selector', action: clear_query, guide: 'sidebar-enrich' },
        {
          label: 'Flow Search',
          icon: ['fas', 'magnifying-glass-chart'],
          children: [
            { label: 'Search', icon: ['fas', 'magnifying-glass-chart'], route: '/search/event', action: clear_query, lock: !hasEventAccess.value },
            { label: 'Saved searches', icon: ['fas', 'bookmark'], route: '/saved-searches' },
          ],
        },
      ],
    },
    {
      label: 'Modules',
      items: [
        { label: 'Dashboards', icon: ['fas', 'table-cells-large'], route: '/dashboard', lock: !hasEventAccess.value },
        { label: 'Signals', icon: ['fas', 'satellite-dish'], route: '/signals', action: clear_query },
        { label: 'Notebooks', icon: ['fas', 'flask'], route: '/notebook', visible: developerEmails.includes(userStore.email) },
        { label: 'File hunt', icon: ['fas', 'file-code'], shortcut: true, disabled: true },
      ],
    },
    {
      label: 'Selector Context',
      items: [
        { label: 'Anonymization', icon: ['fas', 'user-secret'], shortcut: true, slug: 'webscout_anonymization', dot: enrichmentOn('webscout_anonymization') },
        { label: 'Geolocation & Network', icon: ['fas', 'location-dot'], shortcut: true, slug: 'webscout_geo', dot: enrichmentOn('webscout_geo') },
        { label: 'pDNS & Domain Intelligence', icon: ['fas', 'globe'], shortcut: true, dot: false },
        { label: 'Manage enrichments', icon: ['fas', 'diagram-project'], route: '/selector/integrations' },
      ],
    },
    {
      label: 'Flow Sources',
      items: [
        {
          label: groupBySlug('covert-networks')?.display_name || 'Covert Networks',
          icon: ['fas', 'database'],
          shortcut: true,
          sourceSlug: 'covert-networks',
          dot: !!groupBySlug('covert-networks'),
        },
        {
          label: capFirst(groupBySlug('honeypots')?.display_name || 'Honeypots'),
          icon: ['fas', 'database'],
          shortcut: true,
          sourceSlug: 'honeypots',
          dot: !!groupBySlug('honeypots'),
        },
        { label: 'Manage sources', icon: ['fas', 'plug'], route: '/flow/integrations' },
      ],
    },
    {
      label: 'General',
      items: [
        { label: 'API Docs', icon: ['fas', 'code'], route: '/docs/api' },
        { label: 'News', icon: ['fas', 'newspaper'], route: '/news' },
        {
          label: 'Account',
          icon: ['fas', 'user-astronaut'],
          badge: mainStore.has_unread_notifications,
          children: [
            { label: 'Settings', icon: ['fas', 'user-astronaut'], route: '/account' },
            { label: 'Api key', icon: ['fas', 'key'], route: '/account/apikeys' },
            { label: 'Notifications', icon: ['fas', 'bell'], route: '/notifications', badge: mainStore.has_unread_notifications },
            { label: 'Logout', icon: ['fas', 'lock'], action: logout, variant: 'danger' },
          ],
        },
        {
          label: 'Admin tools',
          icon: ['fas', 'gear'],
          children: adminTools,
          visible: userStore.accountType == 1337,
        },
      ],
    },
  ]
  return secs.map(s => ({
    ...s,
    items: s.items
      .filter(i => i.visible !== false)
      // A selector-context shortcut whose enrichment has a dashboard AND is
      // enabled (active) for this org stops being a static label and becomes a
      // link to /selector/integration/<slug>. A Flow Sources shortcut whose
      // group/source exists becomes a link to its detail page
      // (/flow/group|source/<id>). Either way the status dot is preserved
      // through the normal nav-item render path; unresolved shortcuts stay static.
      .map(i => {
        if (i.shortcut && i.slug && enrichmentHasDashboard(i.slug) && enrichmentOn(i.slug)) {
          return { ...i, shortcut: false, route: { name: 'SelectorIntegration', params: { slug: i.slug } } }
        }
        if (i.shortcut && i.sourceSlug) {
          const route = sourceRouteForSlug(i.sourceSlug)
          if (route) return { ...i, shortcut: false, route }
        }
        return i
      }),
  }))
})

// Sidebar expansion state
const isExpanded = ref(false)
const emit = defineEmits(['update:expanded'])

onMounted(() => {
  // Load saved state from localStorage
  const saved = localStorage.getItem('app-sidebar-expanded')
  if (saved !== null) {
    isExpanded.value = saved === 'true'
  }
  // Emit initial state
  emit('update:expanded', isExpanded.value)

  // The Selector Context status dots read enrichment state from the store, but
  // the sidebar lives in the persistent shell — no view guarantees the store is
  // loaded on a fresh landing. init() is idempotent, so drive it here so the
  // dots reflect real on/off state from first paint.
  dataSourcesStore.init()
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  localStorage.setItem('app-sidebar-expanded', isExpanded.value.toString())
  // Emit the new state
  emit('update:expanded', isExpanded.value)
}

async function logout() {
  await userStore.logout()
}

function clear_query() {
  queryStore.result = null
  queryStore.error = ""
  return true
}
</script>

<template>
  <div class="sidebar-wrapper">
    <nav id="sidebar" :class="{ 'expanded': isExpanded }">
      <!-- Header row: logo left, collapse toggle top-right when expanded -->
      <div class="sidebar-header">
        <router-link class="logo-nav" to="/home">
          <img src="~@/assets/ws_logo_idea.svg" class="logo" height="30"/>
          <img src="~@product/assets/logo_white.svg" class="logo-text" height="20"/>
        </router-link>
        <button
          v-if="isExpanded"
          class="collapse-toggle"
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          @click="toggleExpanded"
        >
          <font-awesome-icon :icon="['fas', 'table-columns']" />
        </button>
        <button
          v-else
          class="collapse-toggle expand-toggle"
          aria-label="Expand sidebar"
          title="Expand sidebar"
          @click="toggleExpanded"
        >
          <font-awesome-icon :icon="['fas', 'table-columns']" />
        </button>
      </div>

      <div class="sidebar-scroll">
        <template v-for="section in sections" :key="section.label">
          <div v-if="isExpanded" class="section-header">{{ section.label }}</div>
          <div v-else class="section-separator" aria-hidden="true"></div>

          <ul class="navbar-nav" ondragstart="return false;">
            <template v-for="item in section.items" :key="item.label">
              <SidebarExpandable
                v-if="item.children"
                :parent-label="item.label"
                :parent-icon="item.icon"
                :children="item.children"
                :is-expanded="isExpanded"
                :parent-badge="item.badge || false"
                hover-open
                parent-navigates
              />
              <li v-else-if="item.shortcut" class="nav-item-wrapper">
                <div
                  class="sidebar-nav nav-static"
                  :class="{ 'nav-inactive': item.dot === false || item.disabled }"
                >
                  <span class="icon-wrap">
                    <font-awesome-icon class="icon" :icon="item.icon" />
                    <span
                      v-if="item.dot !== undefined && !isExpanded"
                      class="status-dot status-dot-overlay"
                      :class="{ 'on': item.dot }"
                    ></span>
                  </span>
                  <span class="nav-text">{{ item.label }}</span>
                  <span
                    v-if="item.dot !== undefined && isExpanded"
                    class="status-dot status-dot-inline"
                    :class="{ 'on': item.dot }"
                  ></span>
                  <div class="nav-popout" v-if="!isExpanded">
                    <span>{{ item.label }}</span>
                  </div>
                </div>
              </li>
              <li v-else class="nav-item-wrapper">
                <router-link
                  class="sidebar-nav"
                  :class="{ 'nav-inactive': item.dot === false }"
                  :to="item.route"
                  :data-guide="item.guide"
                  @click="item.action && item.action()"
                >
                  <span class="icon-wrap">
                    <font-awesome-icon class="icon" :icon="item.icon" />
                    <font-awesome-icon v-if="item.lock" class="nav-lock-icon" :icon="['fas', 'lock']" />
                    <span
                      v-if="item.dot !== undefined && !isExpanded"
                      class="status-dot status-dot-overlay"
                      :class="{ 'on': item.dot }"
                    ></span>
                  </span>
                  <span class="nav-text">{{ item.label }}</span>
                  <span
                    v-if="item.dot !== undefined && isExpanded"
                    class="status-dot status-dot-inline"
                    :class="{ 'on': item.dot }"
                  ></span>
                  <div class="nav-popout" v-if="!isExpanded">
                    <span>{{ item.label }}</span>
                  </div>
                </router-link>
              </li>
            </template>
          </ul>
        </template>
      </div>

    </nav>
  </div>
</template>

<style scoped>
.sidebar-wrapper {
  position: relative;
  padding: 1rem;
  z-index: 10;
  transition: width 0.3s ease;
  background-color: var(--surface-page);
}

#sidebar {
  padding: 0;
  background: var(--surface-card);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 85px;
  height: calc(100vh - 2rem);
  border-radius: var(--radius-xl);
  transition: width 0.3s ease;
}

#sidebar.expanded {
  width: 300px;
}

/* Header row — logo + collapse toggle */
.sidebar-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.75rem 0.25rem;
  flex-shrink: 0;
}

.logo-nav {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  min-height: 3rem;
  border-radius: var(--radius-lg);
  min-width: 0;
}

#sidebar:not(.expanded) .sidebar-header {
  justify-content: center;
}

#sidebar:not(.expanded) .logo-nav {
  justify-content: center;
  padding: 0.75rem 0;
}

#sidebar .logo {
  transition: margin-right 0.3s ease;
}

#sidebar.expanded .logo {
  margin-right: 0.75rem;
}

.collapse-toggle {
  margin-left: auto;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
}

.collapse-toggle:hover {
  background-color: var(--surface-hover);
  color: white;
}

/* Collapsed rail: hovering the header animates the logo out and the expand
   toggle in. The toggle fills nearly the whole header footprint (logo-sized,
   not a small centered button) so it's already under the cursor wherever the
   hover started — no extra travel to click it. Overlaid, so nothing jumps. */
.sidebar-header {
  position: relative;
}

/* Item-width (0.75rem side insets) but spanning the logo's full vertical
   footprint (top/bottom insets mirror the header padding). */
#sidebar:not(.expanded) .expand-toggle {
  position: absolute;
  left: 0.75rem;
  right: 0.75rem;
  top: 0.75rem;
  bottom: 0.25rem;
  width: auto;
  height: auto;
  margin: 0;
  font-size: 1.1rem;
  border-radius: var(--radius-lg);
  opacity: 0;
  transform: scale(0.85);
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

#sidebar:not(.expanded) .sidebar-header:hover .expand-toggle {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
}

#sidebar:not(.expanded) .logo-nav {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

#sidebar:not(.expanded) .sidebar-header:hover .logo-nav {
  opacity: 0;
  transform: scale(0.85);
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  #sidebar:not(.expanded) .expand-toggle,
  #sidebar:not(.expanded) .logo-nav {
    transition: opacity 0.2s ease;
    transform: none;
  }
}

/* Scrolling nav body — sections flow top-down */
.sidebar-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: var(--border-subtle) transparent;
}

.sidebar-scroll::-webkit-scrollbar {
  width: 6px;
}

.sidebar-scroll::-webkit-scrollbar-thumb {
  background: var(--border-subtle);
  border-radius: var(--radius-pill);
}

/* Collapsed rail: hover popouts must escape the rail, so no clipping */
#sidebar:not(.expanded) .sidebar-scroll {
  overflow: visible;
}


/* Section headers (expanded) / separators (collapsed) */
/* Receded below item text (--text-secondary) so headers read as structure,
   not as smaller items */
.section-header {
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-disabled);
  padding: 1.75rem 1.75rem 0.375rem;
  white-space: nowrap;
}

.sidebar-scroll > .section-header:first-child {
  padding-top: 0.75rem;
}

.section-separator {
  width: 60%;
  margin: 1rem auto;
  border-top: 1px solid var(--border-subtle);
}

.navbar-nav {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

#sidebar .sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
}

#sidebar:not(.expanded) .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#sidebar .sidebar-nav .icon {
  transition: margin-right 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Text visibility in collapsed state */
#sidebar:not(.expanded) .nav-text {
  display: none;
}

#sidebar:not(.expanded) .logo-text {
  display: none;
}

/* Text visibility in expanded state */
#sidebar.expanded .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

#sidebar.expanded .logo-text {
  display: inline;
  white-space: nowrap;
}

/* Popout styles for collapsed state */
.nav-popout {
  display: flex;
  position: absolute;
  left: calc(100% - 1.3rem);
  top: 0;
  height: 100%;
  background: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

/* Show popout and background on hover of nav-item-wrapper */
#sidebar:not(.expanded) .nav-item-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

#sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav:not(.nav-static) {
  background-color: var(--surface-hover);
  color: white;
}

.nav-popout:hover {
  color: white;
  text-decoration: none;
}

/* Hover effects - same for both collapsed and expanded */
#sidebar .sidebar-nav:not(.nav-static):hover {
  color: white;
  cursor: pointer;
  background-color: var(--surface-hover);
}

/* Active state — same treatment as hover */
.router-link-active.sidebar-nav,
.router-link-exact-active.sidebar-nav {
  background-color: var(--surface-hover);
  color: white !important;
}

/* Enrichment/source shortcuts are informational labels, not links.
   Mirrors the event-search toolbar's disabled state: not-allowed cursor
   plus a muted hover so the row responds without inviting a click. The
   collapsed-rail popout still shows so their labels stay discoverable. */
#sidebar .sidebar-nav.nav-static {
  cursor: not-allowed;
}

#sidebar .sidebar-nav.nav-static:hover,
#sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav.nav-static {
  background-color: var(--surface-card-hover);
}

/* Muted popout for static items — applied at base so the fade-out never
   flashes the enabled colors, and re-asserted on hover to beat the global
   .nav-popout:hover rule. */
#sidebar .sidebar-nav.nav-static .nav-popout,
#sidebar .sidebar-nav.nav-static .nav-popout:hover {
  background: var(--surface-card-hover);
  color: var(--text-disabled);
  cursor: not-allowed;
}

/* Shortcuts whose integration isn't enabled read as disabled (like the
   event-search toolbar). Static items keep the dim even on hover — there's
   no click affordance to signal. */
#sidebar .sidebar-nav.nav-inactive:not(:hover),
#sidebar .sidebar-nav.nav-static.nav-inactive {
  color: var(--text-disabled);
}


/* Icon wrap for dot/badge overlays */
.icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2rem;
  flex-shrink: 0;
}

#sidebar.expanded .sidebar-nav .icon-wrap {
  margin-right: 0.75rem;
  transition: all 0.2s ease;
}

.icon-wrap .icon {
  margin-right: 0 !important;
}

/* Lock badge for tier-restricted items (mirrors SidebarExpandable's .child-lock-icon) */
.nav-lock-icon {
  position: absolute;
  top: -3px;
  right: -5px;
  font-size: 0.35rem;
  color: var(--text-muted);
  background: var(--surface-raised);
  border-radius: var(--radius-pill);
  width: 10px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Status dots — data-source on/off indicators */
.status-dot {
  background: var(--text-disabled);
  border-radius: var(--radius-pill);
  flex-shrink: 0;
  pointer-events: none;
}

.status-dot.on {
  background: var(--color-success);
}

/* Expanded: 7px circle after the label */
.status-dot-inline {
  width: 7px;
  height: 7px;
  margin-left: 0.6rem;
}

/* Collapsed: overlay on the icon's top-right corner, ringed like a badge */
.status-dot-overlay {
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border: 2px solid var(--surface-card);
  box-sizing: content-box;
}
</style>

<!-- Unscoped: theme SidebarExpandable's active state to match item hover.
     The dropdown menu is teleported to <body>, so it can't inherit from #sidebar. -->
<style>
#sidebar,
.expandable-dropdown-menu {
  --sidebar-active-bg: var(--surface-hover);
  --sidebar-active-color: #fff;
}

/* Collapsed dropdown: same flat style as the hover popout labels — no border,
   no shadow, --surface-hover panel. Item hover/active need a lighter overlay
   to stay visible against that background. */
.expandable-dropdown-menu {
  --sidebar-menu-bg: var(--surface-hover);
  --sidebar-menu-border: transparent;
  --sidebar-menu-shadow: none;
  --sidebar-menu-font-size: 0.95rem;
  --sidebar-menu-bridge-bg: var(--surface-hover);
  --sidebar-hover-bg: var(--surface-overlay-3);
  --sidebar-active-bg: var(--surface-overlay-3);
}
</style>
